import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageMessage } from './shared';
import { useProtectionActivity } from './use-protection-activity';

/**
 * "V7" banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring is in the V7 stage
 * (`phenology === 'emergence'` with `vegetativeStage === 7`) and no fungicide/insecticide
 * application or recommendation is registered yet. Same title/actions as V3's protection
 * message, but the copy invites coordinating a field walk with the grower.
 *
 * Returns `null` outside V7, for non-advisor users, or once some fungicide/insecticide activity
 * is registered.
 */
export const useV7StageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const isCornV7 =
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        corn?.phenology === 'emergence' &&
        corn?.vegetativeStage === 7;

    const showCandidate = isValidatedBayerUserType && isCornV7;

    const protection = useProtectionActivity({ cropMonitoring, field, enabled: showCandidate });

    if (!showCandidate) return null;

    // Hold until the gating queries resolve, then only prompt while no protection activity exists.
    if (protection.isLoading) return null;
    if (protection.hasActivity) return null;

    return {
        title: _('Ayudá a proteger el rendimiento del cultivo'),
        message: _('Coordiná una recorrida con el productor para monitorear plagas y enfermedades a tiempo.'),
        actions: [
            { label: _('Registrar aplicación'), variant: 'contained', action: 'register-application' },
            { label: _('Generar recomendación'), variant: 'outlined', action: 'generate-cp-recommendation' },
        ],
    };
};
